// frontend/src/components/admin/LicenceManager.jsx

import { useState, useEffect } from 'react';
import {
  Shield, Plus, RefreshCw, AlertCircle, CheckCircle2,
  X, ChevronDown, ChevronUp, Zap, Info,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// CONFIG
// ============================================================================
const CATEGORIES = {
  finance:   { label: 'Finance',             color: '#22c55e', bg: 'rgba(34,197,94,0.08)'   },
  documents: { label: 'Documents',           color: '#3b82f6', bg: 'rgba(59,130,246,0.08)'  },
  pointage:  { label: 'Pointage',            color: '#f59e0b', bg: 'rgba(245,158,11,0.08)'  },
  rh:        { label: 'Ressources Humaines', color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)'  },
  premium:   { label: 'Premium',             color: '#ef4444', bg: 'rgba(239,68,68,0.08)'   },
  general:   { label: 'Général',             color: '#9ca3af', bg: 'rgba(156,163,175,0.08)' },
};

const LICENCE_COLORS = {
  basique:  { color: '#3b82f6', bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.25)'  },
  standard: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)',  border: 'rgba(139,92,246,0.25)'  },
  premium:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.25)'  },
};

// ============================================================================
// TOGGLE
// ============================================================================
const Toggle = ({ checked, onChange, loading, color = '#448696' }) => (
  <button onClick={() => !loading && onChange(!checked)} disabled={loading}
    style={{ width: 42, height: 24, borderRadius: 12, border: 'none', cursor: loading ? 'wait' : 'pointer',
      background: checked ? color : '#d1d5db', position: 'relative', transition: 'background 0.2s',
      flexShrink: 0, opacity: loading ? 0.6 : 1 }}>
    <span style={{ position: 'absolute', top: 3, left: checked ? 21 : 3,
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
  </button>
);

// ============================================================================
// MODAL — Nouveau flag
// ============================================================================
const NouveauFlagModal = ({ onSave, onClose, theme: t }) => {
  const [form, setForm] = useState({ flag_key: '', label: '', description: '', categorie: 'general', is_extension: false, prix_defaut: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!form.flag_key || !form.label) { setError('flag_key et label requis'); return; }
    setSaving(true);
    try { await onSave(form); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8, background: t.bgInput,
    border: `1px solid ${t.border}`, color: t.text, fontSize: 12,
    boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 460, borderRadius: 18, background: t.bgCard, border: `1px solid ${t.border}`, overflow: 'hidden' }}>

        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 15, fontWeight: 800, color: t.text, margin: 0 }}>Nouveau flag</p>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${t.border}`,
            background: t.bgInput, color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} />
          </button>
        </div>

        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 12, color: '#ef4444' }}>{error}</div>}

          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Clé technique (flag_key) *</label>
            <input value={form.flag_key} onChange={e => setForm(f => ({ ...f, flag_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
              placeholder="ex: mon_nouveau_module" style={inputStyle}
              onFocus={e => e.target.style.borderColor = t.primary} onBlur={e => e.target.style.borderColor = t.border} />
            <p style={{ fontSize: 10, color: t.textMuted, marginTop: 3 }}>Minuscules et underscores uniquement.</p>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Nom affiché *</label>
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="ex: Mon Nouveau Module" style={inputStyle}
              onFocus={e => e.target.style.borderColor = t.primary} onBlur={e => e.target.style.borderColor = t.border} />
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Catégorie</label>
            <select value={form.categorie} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))} style={inputStyle}>
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} placeholder="Description courte du module..." style={{ ...inputStyle, resize: 'vertical' }}
              onFocus={e => e.target.style.borderColor = t.primary} onBlur={e => e.target.style.borderColor = t.border} />
          </div>

          {/* Toggle extension */}
          <div style={{ padding: '12px 14px', borderRadius: 10,
            background: form.is_extension ? 'rgba(245,158,11,0.08)' : t.bgInput,
            border: `1px solid ${form.is_extension ? 'rgba(245,158,11,0.3)' : t.border}`, transition: 'all 0.2s' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: form.is_extension ? 12 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Zap size={13} style={{ color: form.is_extension ? '#f59e0b' : t.textMuted }} />
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.text, margin: 0 }}>Extension payante</p>
                  <p style={{ fontSize: 10, color: t.textMuted, margin: 0 }}>Non inclus dans les licences — facturation séparée</p>
                </div>
              </div>
              <button onClick={() => setForm(f => ({ ...f, is_extension: !f.is_extension }))}
                style={{ width: 38, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                  background: form.is_extension ? '#f59e0b' : '#d1d5db', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <span style={{ position: 'absolute', top: 2, left: form.is_extension ? 18 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </button>
            </div>
            {form.is_extension && (
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Prix de référence (FCFA/mois)</label>
                <input type="number" min="0" value={form.prix_defaut} onChange={e => setForm(f => ({ ...f, prix_defaut: e.target.value }))}
                  placeholder="ex: 15000" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#f59e0b'} onBlur={e => e.target.style.borderColor = t.border} />
                <p style={{ fontSize: 10, color: t.textMuted, marginTop: 3 }}>Prix pré-rempli à l'activation — modifiable par entreprise.</p>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 9, background: t.primary, border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <Plus size={13} />}
            {saving ? 'Ajout...' : 'Ajouter le flag'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL — Convertir en extension
// ============================================================================
const ExtensionModal = ({ flag, onConfirm, onCancel, theme: t }) => {
  const [prix, setPrix] = useState(flag.prix_defaut || '');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm(flag.flag_key, true, parseFloat(prix) || 0); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420, borderRadius: 18, background: t.bgCard, border: `1px solid ${t.border}`, overflow: 'hidden' }}>

        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={18} style={{ color: '#f59e0b' }} />
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 800, color: t.text, margin: 0 }}>Convertir en extension</p>
            <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{flag.label}</p>
          </div>
        </div>

        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
            Ce flag sera <strong>retiré de la matrice des licences</strong> et deviendra un module à la demande, activable et facturé séparément par entreprise.
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Prix de référence (FCFA/mois)</label>
            <input type="number" min="0" value={prix} onChange={e => setPrix(e.target.value)}
              placeholder="ex: 15000" autoFocus
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, fontWeight: 700, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
              onFocus={e => e.target.style.borderColor = '#f59e0b'} onBlur={e => e.target.style.borderColor = t.border} />
            <p style={{ fontSize: 10, color: t.textMuted, marginTop: 3 }}>Prix pré-rempli à l'activation — modifiable par entreprise.</p>
          </div>
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onCancel} style={{ padding: '9px 18px', borderRadius: 9, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleConfirm} disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 9, background: '#f59e0b', border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <Zap size={13} />}
            {saving ? 'Conversion...' : 'Convertir en extension'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const LicenceManager = () => {
  const t = useTheme();

  const [licences,    setLicences]    = useState([]);
  const [flags,       setFlags]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [toggling,    setToggling]    = useState({});
  const [togglingExt, setTogglingExt] = useState({});
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [showNewFlag, setShowNewFlag] = useState(false);
  const [collapsed,   setCollapsed]   = useState({});
  const [extModal,    setExtModal]    = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.licences.getAll();
      if (res.success) {
        setLicences(res.data.licences || []);
        setFlags(res.data.flags || []);
      }
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const flagsNormaux    = flags.filter(f => !f.is_extension);
  const flagsExtensions = flags.filter(f =>  f.is_extension);

  const flagsNormauxByCategorie = flagsNormaux.reduce((acc, f) => {
    if (!acc[f.categorie]) acc[f.categorie] = [];
    acc[f.categorie].push(f);
    return acc;
  }, {});

  const handleToggle = async (licenceId, flagKey, currentValue) => {
    const key = `${licenceId}_${flagKey}`;
    setToggling(prev => ({ ...prev, [key]: true }));
    setError(''); setSuccess('');
    try {
      const res = await superAdminApi.licences.setFlag(licenceId, flagKey, !currentValue);
      if (!res.success) { setError(res.message); return; }
      setLicences(prev => prev.map(l => l.id !== licenceId ? l : { ...l, flags: { ...l.flags, [flagKey]: !currentValue ? 1 : 0 } }));
      setSuccess(res.message || 'Mis à jour');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Erreur réseau'); }
    finally { setToggling(prev => ({ ...prev, [key]: false })); }
  };

  const handleSetExtension = async (flagKey, isExtension, prixDefaut) => {
    setTogglingExt(prev => ({ ...prev, [flagKey]: true }));
    setError(''); setSuccess('');
    try {
      const res = await superAdminApi.licences.setExtension(flagKey, isExtension, prixDefaut);
      if (!res.success) { setError(res.message); return; }
      setSuccess(res.message);
      setTimeout(() => setSuccess(''), 3000);
      setExtModal(null);
      await loadData();
    } catch { setError('Erreur réseau'); }
    finally { setTogglingExt(prev => ({ ...prev, [flagKey]: false })); }
  };

  const handleNewFlag = async (form) => {
    const res = await superAdminApi.licences.addFlag(form);
    if (!res.success) throw new Error(res.message);
    if (form.is_extension) {
      await superAdminApi.licences.setExtension(form.flag_key, true, parseFloat(form.prix_defaut) || 0);
    }
    setSuccess('Nouveau flag ajouté !');
    setTimeout(() => setSuccess(''), 3000);
    setShowNewFlag(false);
    await loadData();
  };

  const toggleCategorie = (cat) => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));

  if (loading) return (
    <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <RefreshCw size={20} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: t.textMuted }}>Chargement…</span>
    </div>
  );

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>Gestion des licences</h1>
          <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>
            Définissez les modules inclus dans chaque licence. Les modifications s'appliquent automatiquement aux entreprises concernées.
          </p>
        </div>
        <button onClick={() => setShowNewFlag(true)} style={{ display: 'flex', alignItems: 'center', gap: 7,
          padding: '10px 18px', borderRadius: 10, background: t.primarySoft, color: t.primary,
          border: `1px solid ${t.primaryBorder}`, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <Plus size={14} /> Nouveau flag
        </button>
      </div>

      {/* ALERTES */}
      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8 }}>
          <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', display: 'flex', gap: 8 }}>
          <CheckCircle2 size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#22c55e' }}>{success}</span>
        </div>
      )}

      {/* INCLUS DE BASE */}
      <div style={{ borderRadius: 16, border: `1px solid ${t.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', background: 'rgba(34,197,94,0.06)', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.1em', flex: 1 }}>Inclus dans toutes les licences</span>
          <span style={{ fontSize: 11, color: t.textMuted, fontStyle: 'italic' }}>Toujours actif — non configurable</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {[
            { icon: '🏠', label: 'Tableau de bord',    desc: 'KPIs et résumé en temps réel'         },
            { icon: '👥', label: 'Ressources Humaines', desc: 'Liste, création et profil employés'    },
            { icon: '🕐', label: 'Carnet de pointage',  desc: 'Pointage manuel (entrée / sortie)'     },
            { icon: '📊', label: 'Rapport global',      desc: 'Présences, absences, synthèse basique' },
            { icon: '⚙️', label: 'Paramètres',          desc: 'Entreprise, utilisateurs, droits'      },
          ].map((item, i, arr) => (
            <div key={item.label} style={{ padding: '16px 18px', background: t.bgCard,
              borderRight: i < arr.length - 1 ? `1px solid ${t.borderLight}` : 'none',
              display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                  background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)', textTransform: 'uppercase' }}>✓ Inclus</span>
              </div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: t.text, margin: '0 0 3px' }}>{item.label}</p>
                <p style={{ fontSize: 11, color: t.textMuted, margin: 0, lineHeight: 1.4 }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MATRICE LICENCES */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px repeat(3, 1fr)', borderRadius: 16, border: `1px solid ${t.border}`, overflow: 'hidden' }}>

        <div style={{ padding: '16px 20px', background: t.bgTableHeader, borderRight: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}` }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Module / Flag</p>
        </div>

        {licences.map((licence, i) => {
          const lc = LICENCE_COLORS[licence.slug] || LICENCE_COLORS.basique;
          const enabledCount = Object.values(licence.flags || {}).filter(v => v === 1).length;
          return (
            <div key={licence.id} style={{ padding: '16px 20px', background: t.bgTableHeader,
              borderRight: i < licences.length - 1 ? `1px solid ${t.border}` : 'none',
              borderBottom: `1px solid ${t.border}`, textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: lc.color }} />
                <p style={{ fontSize: 14, fontWeight: 900, color: lc.color, margin: 0 }}>{licence.nom}</p>
              </div>
              <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{enabledCount} module{enabledCount !== 1 ? 's' : ''} actif{enabledCount !== 1 ? 's' : ''}</p>
            </div>
          );
        })}

        {Object.keys(flagsNormauxByCategorie).length === 0 ? (
          <div style={{ gridColumn: '1 / -1', padding: '30px 20px', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>Tous les flags sont des extensions.</p>
          </div>
        ) : Object.entries(flagsNormauxByCategorie).map(([categorie, catFlags]) => {
          const catConfig = CATEGORIES[categorie] || CATEGORIES.general;
          const isCollapsedCat = collapsed[categorie];
          return [
            <div key={`cat-${categorie}`} style={{ gridColumn: '1 / -1', padding: '8px 20px',
              background: catConfig.bg, borderBottom: `1px solid ${t.border}`,
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
              onClick={() => toggleCategorie(categorie)}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: catConfig.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: catConfig.color, textTransform: 'uppercase', letterSpacing: '0.1em', flex: 1 }}>{catConfig.label}</span>
              <span style={{ fontSize: 11, color: t.textMuted }}>{catFlags.length} flag{catFlags.length > 1 ? 's' : ''}</span>
              {isCollapsedCat ? <ChevronDown size={13} style={{ color: t.textMuted }} /> : <ChevronUp size={13} style={{ color: t.textMuted }} />}
            </div>,

            ...(!isCollapsedCat ? catFlags.map((flag) => [
              <div key={`flag-label-${flag.flag_key}`} style={{ padding: '13px 20px', background: t.bgCard,
                borderRight: `1px solid ${t.border}`, borderBottom: `1px solid ${t.borderLight}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: t.text, margin: '0 0 2px' }}>{flag.label}</p>
                    {flag.description && <p style={{ fontSize: 11, color: t.textMuted, margin: 0, lineHeight: 1.4 }}>{flag.description}</p>}
                    <code style={{ fontSize: 9, color: t.textMuted, background: t.bgInput, padding: '1px 5px', borderRadius: 4, marginTop: 3, display: 'inline-block' }}>{flag.flag_key}</code>
                  </div>
                  <button onClick={() => setExtModal(flag)} disabled={!!togglingExt[flag.flag_key]} title="Convertir en extension payante"
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6,
                      border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)',
                      color: '#f59e0b', fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <Zap size={10} /> Ext.
                  </button>
                </div>
              </div>,

              ...licences.map((licence, li) => {
                const lc      = LICENCE_COLORS[licence.slug] || LICENCE_COLORS.basique;
                const enabled = licence.flags?.[flag.flag_key] === 1;
                const tKey    = `${licence.id}_${flag.flag_key}`;
                return (
                  <div key={`toggle-${licence.id}-${flag.flag_key}`} style={{ padding: '13px 20px', background: t.bgCard,
                    borderRight: li < licences.length - 1 ? `1px solid ${t.border}` : 'none',
                    borderBottom: `1px solid ${t.borderLight}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Toggle checked={enabled} onChange={() => handleToggle(licence.id, flag.flag_key, enabled)}
                      loading={!!toggling[tKey]} color={lc.color} />
                  </div>
                );
              }),
            ]).flat() : []),
          ];
        })}
      </div>

      {/* EXTENSIONS */}
      {flagsExtensions.length > 0 && (
        <div style={{ borderRadius: 16, border: '1px solid rgba(245,158,11,0.3)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', background: 'rgba(245,158,11,0.06)', borderBottom: '1px solid rgba(245,158,11,0.2)',
            display: 'flex', alignItems: 'center', gap: 10 }}>
            <Zap size={14} style={{ color: '#f59e0b' }} />
            <span style={{ fontSize: 11, fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.1em', flex: 1 }}>
              Extensions payantes ({flagsExtensions.length})
            </span>
            <span style={{ fontSize: 11, color: '#92400e', fontStyle: 'italic' }}>Non inclus dans les licences — facturation séparée</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {flagsExtensions.map((flag, i) => (
              <div key={flag.flag_key} style={{ padding: '14px 20px', background: t.bgCard,
                borderBottom: i < flagsExtensions.length - 1 ? `1px solid ${t.borderLight}` : 'none',
                display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(245,158,11,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Zap size={15} style={{ color: '#f59e0b' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>{flag.label}</p>
                    <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                      background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)', textTransform: 'uppercase' }}>EXTENSION</span>
                  </div>
                  {flag.description && <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{flag.description}</p>}
                  <code style={{ fontSize: 9, color: t.textMuted, background: t.bgInput, padding: '1px 5px', borderRadius: 4, marginTop: 3, display: 'inline-block' }}>{flag.flag_key}</code>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 800, color: '#f59e0b', margin: '0 0 2px' }}>
                    {flag.prix_defaut > 0 ? `${Number(flag.prix_defaut).toLocaleString('fr-FR')} FCFA` : 'Prix libre'}
                  </p>
                  <p style={{ fontSize: 10, color: t.textMuted, margin: 0 }}>par mois / référence</p>
                </div>
                <button onClick={() => handleSetExtension(flag.flag_key, false, 0)} disabled={!!togglingExt[flag.flag_key]}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8,
                    border: `1px solid ${t.border}`, background: t.bgInput, color: t.textMuted,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                  {togglingExt[flag.flag_key]
                    ? <span style={{ width: 11, height: 11, border: '2px solid rgba(0,0,0,0.1)', borderTopColor: t.textMuted, borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                    : <Shield size={11} />}
                  Remettre en licence
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NOTE */}
      <div style={{ padding: '12px 16px', borderRadius: 12, background: t.primarySoft, border: `1px solid ${t.primaryBorder}`, display: 'flex', gap: 10 }}>
        <Info size={14} style={{ color: t.primary, flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 12, color: t.textSecondary, margin: 0, lineHeight: 1.6 }}>
          <strong style={{ color: t.primary }}>Propagation automatique :</strong> Activer ou désactiver un flag met à jour toutes les entreprises ayant cette licence, sauf celles avec des overrides manuels.
          Les <strong style={{ color: '#f59e0b' }}>extensions</strong> sont gérées séparément via Feature Flags.
        </p>
      </div>

      {showNewFlag && <NouveauFlagModal onSave={handleNewFlag} onClose={() => setShowNewFlag(false)} theme={t} />}
      {extModal && <ExtensionModal flag={extModal} onConfirm={handleSetExtension} onCancel={() => setExtModal(null)} theme={t} />}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default LicenceManager;